import React, { useState, Component } from 'react';
/**
 * ExplanationContent — Conversational explanation with Djay intros.
 *
 * Shows distractor explanation (why wrong) and main explanation (how to solve)
 * with Djay avatar intros and numbered solution steps.
 *
 * Co-located in practice/ domain (CL-1 s64).
 */
import { motion } from 'framer-motion';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { DjayAvatar, useDjaySelection, getDjayDistractorIntro, getDjayExplanationIntro } from '../../../mascot';
import { ExplanationSteps } from './ExplanationSteps';
// ── Types ────────────────────────────────────────────────────
interface ExplanationContentProps {
  explanation: string;
  distractorExplanation?: string;
  isCorrect: boolean;
  solutionSteps?: string[];
}
// ── Component ────────────────────────────────────────────────
export function ExplanationContent({
  explanation,
  distractorExplanation,
  isCorrect,
  solutionSteps
}: ExplanationContentProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  // useState initializer runs once — prevents random re-pick on scroll/re-render
  const [distractorIntro] = useState(() => getDjayDistractorIntro());
  const [explanationIntro] = useState(() => getDjayExplanationIntro());
  return <div className="space-y-5" data-id="element-7587">
      {/* Distractor explanation (why it was wrong) */}
      {!isCorrect && distractorExplanation && <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-7588">
          <div className="flex items-start gap-3 mb-4" data-id="element-7589">
            <motion.div className="flex-shrink-0 mt-0.5" animate={{
          rotate: [0, -5, 5, 0]
        }} transition={{
          delay: 0.3,
          duration: 0.5
        }} data-id="element-7590">
              <DjayAvatar size="sm" emotion="thinking" variant={selectedVariant} animate={true} data-id="element-7591" />
            </motion.div>
            <div className="min-w-0 flex-1" data-id="element-7592">
              <h4 className="font-bold text-slate-700 text-sm mb-2" data-id="element-7593">
                {distractorIntro}
              </h4>
              <div className="text-slate-600 text-sm leading-relaxed" data-id="element-7594">
                <LatexRenderer content={distractorExplanation} data-id="element-7595" />
              </div>
            </div>
          </div>
          <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent my-5" data-id="element-7596" />
        </motion.div>}

      {/* Main explanation (how to solve it) */}
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: distractorExplanation ? 0.15 : 0,
      duration: 0.3
    }} className="relative" data-id="element-7597">
        <div className="flex items-center gap-2 mb-3" data-id="element-7598">
          <motion.div animate={{
          y: [0, -2, 0]
        }} transition={{
          delay: 0.4,
          duration: 1.5,
          repeat: Infinity,
          repeatType: 'reverse'
        }} data-id="element-7599">
            <DjayAvatar size="sm" emotion="pointing" variant={selectedVariant} animate={true} data-id="element-7600" />
          </motion.div>
          <span className="text-sm font-bold text-orange-600" data-id="element-7601">
            {explanationIntro}
          </span>
        </div>

        <ExplanationSteps text={explanation || (isCorrect ? 'Goed gedaan! Je hebt de juiste strategie gebruikt.' : 'Kijk goed naar de stappen hierboven.')} steps={solutionSteps} variant="conversational" data-id="element-7602" />
      </motion.div>
    </div>;
}